import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
  memo,
} from "react";
import RichTextEditor from "react-rte";

/**
 * RichTextField – a stable, enterprise-friendly wrapper around react-rte
 *
 * Props:
 * - initialValue?: string       // uncontrolled mode starting content
 * - valueHtml?: string         // controlled mode source of truth
 * - onChange?: (html) => void
 * - debounceMs?: number        // default 300ms
 * - disabled?: boolean
 * - placeholder?: string
 * - className?: string
 *
 * Notes:
 * - Use EITHER valueHtml (controlled) OR initialValue (uncontrolled). If both are given, valueHtml wins.
 * - Debounce applies only to onChange upcalls (internal typing is instant).
 */
const RichTextField = forwardRef(
  (
    {
      initialValue = "",
      valueHtml,
      onChange,
      debounceMs = 300,
      disabled = false,
      placeholder = "",
      className = "",
    },
    ref
  ) => {
    // Guard against SSR: react-rte needs window. If SSR, render a fallback.
    const canRenderEditor = typeof window !== "undefined";

    // Internal editor state (react-rte Value)
    const [rteValue, setRteValue] = useState(() =>
      canRenderEditor
        ? valueHtml != null
          ? RichTextEditor.createValueFromString(valueHtml, "html")
          : initialValue
          ? RichTextEditor.createValueFromString(initialValue, "html")
          : RichTextEditor.createEmptyValue()
        : null
    );

    const debounceTimer = useRef(null);
    const lastExternalHtml = useRef(valueHtml ?? initialValue ?? "");

    // Controlled mode: sync when valueHtml changes meaningfully
    useEffect(() => {
      if (!canRenderEditor) return;
      if (valueHtml == null) return; // not in controlled mode

      if (valueHtml !== lastExternalHtml.current) {
        setRteValue(
          RichTextEditor.createValueFromString(valueHtml || "", "html")
        );
        lastExternalHtml.current = valueHtml || "";
      }
    }, [valueHtml, canRenderEditor]);

    // Uncontrolled mode: initialize once from initialValue
    useEffect(() => {
      if (!canRenderEditor) return;
      if (valueHtml != null) return; // controlled takes precedence
      // Only update if initialValue changes and we’re not already at that value
      if (initialValue !== lastExternalHtml.current) {
        setRteValue(
          initialValue
            ? RichTextEditor.createValueFromString(initialValue, "html")
            : RichTextEditor.createEmptyValue()
        );
        lastExternalHtml.current = initialValue || "";
      }
    }, [initialValue, valueHtml, canRenderEditor]);

    const handleChange = (val) => {
      setRteValue(val); // keep typing smooth and local
      const html = val.toString("html");

      if (onChange) {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          lastExternalHtml.current = html;
          onChange(html);
        }, debounceMs);
      }
    };

    // Imperative API for parents
    useImperativeHandle(ref, () => ({
      getHTML: () => (rteValue ? rteValue.toString("html") : ""),
      setHTML: (html = "") => {
        if (!canRenderEditor) return;
        const v = RichTextEditor.createValueFromString(html, "html");
        setRteValue(v);
        lastExternalHtml.current = html;
      },
      clear: () => {
        if (!canRenderEditor) return;
        const v = RichTextEditor.createEmptyValue();
        setRteValue(v);
        lastExternalHtml.current = "";
      },
      focus: () => {
        // react-rte doesn’t expose focus easily; no-op for now
      },
    }));

    const editorProps = useMemo(
      () => ({
        value: rteValue,
        onChange: handleChange,
        readOnly: disabled,
        placeholder,
        toolbarConfig: undefined, // use default; customize if needed
      }),
      [rteValue, disabled, placeholder]
    );

    if (!canRenderEditor) {
      // SSR-friendly fallback
      return (
        <textarea
          className={className}
          defaultValue={valueHtml ?? initialValue ?? ""}
          disabled={disabled}
          placeholder={placeholder}
          style={{ minHeight: 160, width: "100%" }}
        />
      );
    }

    return (
      <div className={className} style={{ minHeight: 160 }}>
        <RichTextEditor {...editorProps} />
      </div>
    );
  }
);

export default memo(RichTextField);
